/**
 * Voice types — Agents as Users Phase 1 (P-AGENT-1).
 * Participant-standing identities: voice, never authority.
 */

export type VoiceStanding = 'participant';
export type VoiceCountingPolicy = 'advisory' | 'counted' | 'weighted';
/**
 * Policies a seat can actually hold in Phase 2 (PRD §4). 'weighted' stays in
 * the DB CHECK for Phase 3 but is unreachable from the app until then.
 */
export type ActiveVoiceCountingPolicy = 'advisory' | 'counted';
export type VoiceCorpusKind = 'support_tickets' | 'crm_pipeline';
export type VoiceStatus = 'active' | 'suspended';
export type VoiceSeatType = 'estimator' | 'advocate' | 'constituency' | 'intake_submitter';
export type VoiceCorpusSourceKind =
  | 'support_ticket'
  | 'crm_deal'
  | 'crm_activity'
  | 'seed'
  | 'connector';

export interface WorkspaceVoice {
  id: string;
  workspaceId: string;
  slug: string;
  displayName: string;
  description: string | null;
  sponsorUserId: string;
  standing: VoiceStanding;
  participationSeats: VoiceSeatType[];
  defaultCountingPolicy: VoiceCountingPolicy;
  corpusKind: VoiceCorpusKind | null;
  status: VoiceStatus;
  badgeLabel: string;
  badgeColor: string;
  createdAt: string;
  updatedAt: string;
}

export interface VoiceMembership {
  id: string;
  voiceId: string;
  orgUnitId: string;
  createdAt: string;
}

export interface VoiceCorpusDocument {
  id: string;
  voiceId: string;
  workspaceId: string;
  sourceKind: VoiceCorpusSourceKind;
  externalRef: string | null;
  title: string;
  body: string;
  occurredAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface SessionVoiceSeat {
  id: string;
  sessionId: string;
  voiceId: string;
  countingPolicy: VoiceCountingPolicy;
  seatType: VoiceSeatType;
  addedBy: string;
  overrideCap: boolean;
  overrideReason: string | null;
  status: 'active' | 'removed';
  addedAt: string;
  /** Facilitator who last changed counting_policy (PRD §4 opt-in attribution). */
  policyChangedBy: string | null;
  policyChangedAt: string | null;
}

/** Phase 1 seat cap: min(2, floor(0.2 * humanRosterSize)), at least 0. */
export function voiceSeatCap(humanRosterSize: number): number {
  if (humanRosterSize <= 0) return 0;
  return Math.min(2, Math.floor(0.2 * humanRosterSize));
}

export function slugifyVoiceName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64) || 'voice';
}
